
/**
 * Watchlist Service
 * Handles watchlist management API calls
 */

import { apiRequest } from './api';

export const WatchlistService = {
    /**
     * Get user's watchlist
     * @returns {Promise<Object>}
     */
    async getWatchlist() {
        return apiRequest('/watchlist');
    },

    /**
     * Check if a specific media item is already on the user's watchlist.
     * Fetches the full watchlist and matches by tmdbId + kind.
     * @param {number} tmdbId
     * @param {string} kind - 'movie' | 'tv'
     * @returns {Promise<boolean>}
     */
    async isOnWatchlist(tmdbId, kind) {
        try {
            const result = await apiRequest('/watchlist');
            const items = result?.data?.items ?? result?.items ?? [];
            return items.some(
                (item) =>
                    String(item.media?.tmdb_id) === String(tmdbId) &&
                    item.media?.kind === kind
            );
        } catch {
            return false;
        }
    },

    /**
     * Add item to watchlist
     * @param {number} tmdbId - TMDB ID
     * @param {string} kind - 'movie' or 'tv'
     * @param {{ source?: string, position?: number }} [meta] - only labels the
     *   server-side funnel event (e.g. source: 'watchlist_onboarding')
     * @returns {Promise<Object>}
     */
    async addToWatchlist(tmdbId, kind, { source, position } = {}) {
        const body = { tmdbId, kind };
        if (source) body.source = source;
        if (position != null) body.position = position;
        return apiRequest('/watchlist', {
            method: 'POST',
            body,
        });
    },

    /**
     * Remove item from watchlist
     * @param {string} itemId - Watchlist item ID
     * @returns {Promise<void>}
     */
    async removeFromWatchlist(itemId) {
        return apiRequest(`/watchlist/${itemId}`, {
            method: 'DELETE',
        });
    },

    /**
     * Update watchlist item status
     * @param {string} itemId - Watchlist item ID
     * @param {string} status - 'saved' or 'watched'
     * @returns {Promise<Object>}
     */
    async updateStatus(itemId, status) {
        return apiRequest(`/watchlist/${itemId}`, {
            method: 'PATCH',
            body: { status },
        });
    },

    /**
     * Update watchlist visibility
     * @param {string} visibility - 'public', 'friends', or 'private'
     * @returns {Promise<Object>}
     */
    async updateVisibility(visibility) {
        return apiRequest('/watchlist/visibility', {
            method: 'PATCH',
            body: { visibility },
        });
    },
};